/**
 * Phase 4 — Customer Lifetime Mini Card.
 *
 * Compact stats grid + favourite-partners list, fetched from /lifetime/me.
 * Used on the Account page next to MyActivityLog.
 */
import { useEffect, useState } from 'react';
import {
  Heart, Eye, Sparkles, MessageSquare, Calendar, ShoppingBag,
  FileText, TrendingUp, Loader2, Crown
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTER_META = [
  { key: 'wishlist_items', label: 'Wishlist', Icon: Heart, tone: 'text-red-300' },
  { key: 'properties_viewed', label: 'Properties Viewed', Icon: Eye, tone: 'text-blue-300' },
  { key: 'bundles_explored', label: 'Bundles Explored', Icon: Sparkles, tone: 'text-purple-300' },
  { key: 'concierge_requests', label: 'Concierge Chats', Icon: MessageSquare, tone: 'text-cyan-300' },
  { key: 'appointments', label: 'Appointments', Icon: Calendar, tone: 'text-amber-300' },
  { key: 'leads', label: 'Active Inquiries', Icon: TrendingUp, tone: 'text-emerald-300' },
  { key: 'quotations_received', label: 'Quotations', Icon: FileText, tone: 'text-gold' },
  { key: 'purchases', label: 'Purchases', Icon: ShoppingBag, tone: 'text-white' },
];

const LifetimeMiniCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/lifetime/me`);
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="glass-card p-6 flex items-center gap-3 text-text-secondary">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your lifetime journey…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="glass-card p-6" data-testid="lifetime-mini-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Crown className="w-5 h-5 text-gold" />
          <h3 className="font-playfair text-xl font-semibold">Your Lifetime Journey</h3>
        </div>
        <span className="text-[10px] uppercase tracking-[0.2em] text-text-secondary">
          {data.user.subscription_tier || 'Free'} · {data.user.region || 'UAE'}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {COUNTER_META.map((c) => (
          <div
            key={c.key}
            className="rounded-lg bg-white/[0.02] border border-white/5 px-3 py-2"
            data-testid={`lifetime-counter-${c.key}`}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-[9px] uppercase tracking-[0.18em] text-text-secondary">{c.label}</span>
              <c.Icon className={`w-3.5 h-3.5 ${c.tone}`} />
            </div>
            <div className={`text-xl font-semibold ${c.tone}`}>
              {data.counters[c.key] ?? 0}
            </div>
          </div>
        ))}
      </div>

      {data.favourite_partners?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-text-secondary mb-2">
            Favourite Partners
          </div>
          <div className="flex flex-wrap gap-2">
            {data.favourite_partners.map((p) => (
              <span
                key={p.partner_id}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gold/10 ring-1 ring-gold/20 text-xs text-white/90"
                data-testid={`favourite-partner-${p.partner_id}`}
              >
                {p.name}
                <span className="text-gold/80 font-semibold">· {p.engagements}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {data.summary_tags?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-text-secondary mb-2">
            Your preferences
          </div>
          <div className="flex flex-wrap gap-2">
            {data.summary_tags.map((t, i) => (
              <span key={i} className="px-2.5 py-1 rounded-full bg-white/5 text-[11px] text-white/80">
                {t}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LifetimeMiniCard;
